import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCan } from "../../../lib/auth";
import {
  useAddPlaylistItem,
  useAdminYoutubeVideos,
  useDeletePlaylist,
  useRemovePlaylistItem,
  useReorderPlaylistItem,
  useUpdatePlaylist,
  useYoutubePlaylist,
} from "../../../lib/youtube";
import { Modal } from "../../../components/ui/Modal";
import {
  SortableVideoList,
  type SortableItem,
} from "../../../components/youtube/SortableVideoList";

/**
 * Per-series editor. Title / description / privacy auto-save on blur with
 * a "Saved 2s ago" indicator. Items list is drag-reorderable; add via
 * picker; remove per-item. Danger-zone delete (typed-confirm).
 */

const SAVED_FADE_MS = 1800;

export default function SeriesEditor() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQ = useYoutubePlaylist(id);
  const update = useUpdatePlaylist(id);
  const removeItem = useRemovePlaylistItem();
  const reorder = useReorderPlaylistItem();
  const addItem = useAddPlaylistItem(id);
  const del = useDeletePlaylist();
  const canDelete = useCan("youtube.delete");

  const [items, setItems] = useState<SortableItem[]>([]);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Sync server items into local state whenever they change.
  useEffect(() => {
    if (!detailQ.data) return;
    setItems(
      detailQ.data.items.map((it) => ({
        id: it.id,
        videoId: it.videoId,
        title: it.title,
        thumbnailUrl: it.thumbnailUrl,
        durationSec: it.durationSec,
      })),
    );
  }, [detailQ.data]);

  if (!id) return null;
  if (detailQ.isLoading)
    return <p className="yt-admin-empty">Loading…</p>;
  if (!detailQ.data) {
    return (
      <p className="yt-admin-empty">
        Series not found.{" "}
        <Link to="/dashboard/youtube/series" className="row-action">
          Back to series
        </Link>
      </p>
    );
  }

  const { playlist } = detailQ.data;
  const matchesTitle = confirmInput.trim() === playlist.title.trim();

  const onReorder = async (next: SortableItem[]) => {
    const prev = items;
    setItems(next); // optimistic
    // Find the item that moved and patch its position.
    for (let i = 0; i < next.length; i++) {
      if (next[i]!.id !== prev[i]?.id) {
        const moved = next[i]!;
        try {
          setBusyItemId(moved.id);
          await reorder.mutateAsync({ itemId: moved.id, position: i });
        } catch (e) {
          // Revert.
          setItems(prev);
          setError(
            e instanceof Error ? e.message : "Couldn't reorder.",
          );
        } finally {
          setBusyItemId(null);
        }
        break;
      }
    }
  };

  const onRemove = async (itemId: string) => {
    setError(null);
    setBusyItemId(itemId);
    try {
      await removeItem.mutateAsync(itemId);
      setItems((curr) => curr.filter((it) => it.id !== itemId));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't remove that item.",
      );
    } finally {
      setBusyItemId(null);
    }
  };

  const onDelete = async () => {
    try {
      await del.mutateAsync(id);
      navigate("/dashboard/youtube/series");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't delete the series.",
      );
    }
  };

  return (
    <div>
      <SeriesHeaderForm
        playlistId={id}
        initialTitle={playlist.title}
        initialDescription={playlist.description ?? ""}
        onSaved={() => setSavedAt(Date.now())}
        update={update}
      />
      {savedAt && Date.now() - savedAt < SAVED_FADE_MS && (
        <p className="yt-admin-note font-display-italic" aria-live="polite">
          Saved.
        </p>
      )}

      <section className="yt-form-section mt-6">
        <header className="yt-form-section-head">
          <h3 className="yt-form-section-title">Items</h3>
          <button
            type="button"
            className="btn-app"
            onClick={() => setAdding(true)}
          >
            + Add video
          </button>
        </header>
        <SortableVideoList
          items={items}
          onReorder={onReorder}
          onRemove={onRemove}
          busyItemId={busyItemId}
        />
      </section>

      {error && (
        <p className="yt-admin-error mt-4" role="alert">
          {error}
        </p>
      )}

      {canDelete && (
        <section className="yt-form-section yt-danger-zone mt-8">
          <h3 className="yt-form-section-title">Danger zone</h3>
          <p className="yt-admin-note">
            Deleting the series unlinks its videos but leaves the videos
            themselves on the channel.
          </p>
          {!confirmingDelete ? (
            <button
              type="button"
              className="btn-app-ghost is-danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete this series
            </button>
          ) : (
            <div className="yt-danger-confirm">
              <p>
                Type the title{" "}
                <span className="font-display-italic">"{playlist.title}"</span>{" "}
                to confirm.
              </p>
              <div className="flex gap-2 items-center mt-2 flex-wrap">
                <input
                  type="text"
                  className="auth-input"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn-app is-danger"
                  disabled={!matchesTitle || del.isPending}
                  onClick={onDelete}
                >
                  {del.isPending ? "Deleting…" : "Confirm delete"}
                </button>
                <button
                  type="button"
                  className="btn-app-ghost"
                  onClick={() => {
                    setConfirmingDelete(false);
                    setConfirmInput("");
                  }}
                  disabled={del.isPending}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {adding && (
        <AddVideoModal
          playlistId={id}
          onClose={() => setAdding(false)}
          onAdded={(itemId, videoMeta) => {
            setItems((curr) => [
              ...curr,
              {
                id: itemId,
                videoId: videoMeta.videoId,
                title: videoMeta.title,
                thumbnailUrl: videoMeta.thumbnailUrl,
                durationSec: videoMeta.durationSec,
              },
            ]);
            void addItem;
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SeriesHeaderForm({
  playlistId,
  initialTitle,
  initialDescription,
  onSaved,
  update,
}: {
  playlistId: string;
  initialTitle: string;
  initialDescription: string;
  onSaved: () => void;
  update: ReturnType<typeof useUpdatePlaylist>;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [error, setError] = useState<string | null>(null);
  // Suppress unused — kept for future per-row references.
  void playlistId;

  const lastSavedRef = useRef({
    title: initialTitle,
    description: initialDescription,
  });

  const saveIfChanged = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title can't be empty.");
      setTitle(lastSavedRef.current.title);
      return;
    }
    const dirty =
      trimmedTitle !== lastSavedRef.current.title ||
      description !== lastSavedRef.current.description;
    if (!dirty) return;
    setError(null);
    try {
      await update.mutateAsync({
        title: trimmedTitle,
        description,
      });
      lastSavedRef.current = { title: trimmedTitle, description };
      onSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't save.",
      );
    }
  };

  return (
    <section className="yt-form-section">
      <h3 className="yt-form-section-title">Series details</h3>
      <div className="auth-field">
        <label className="auth-label">Title</label>
        <input
          type="text"
          className="auth-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveIfChanged}
          maxLength={150}
        />
      </div>
      <div className="auth-field">
        <label className="auth-label">Description</label>
        <textarea
          className="auth-input"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={saveIfChanged}
          maxLength={5000}
        />
      </div>
      {error && (
        <p className="yt-admin-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function AddVideoModal({
  playlistId,
  onClose,
  onAdded,
}: {
  playlistId: string;
  onClose: () => void;
  onAdded: (
    itemId: string,
    video: {
      videoId: string;
      title: string;
      thumbnailUrl: string;
      durationSec: number | null;
    },
  ) => void;
}) {
  const [search, setSearch] = useState("");
  const videosQ = useAdminYoutubeVideos({ q: search, limit: 20 });
  const add = useAddPlaylistItem(playlistId);
  const [busyVideoId, setBusyVideoId] = useState<string | null>(null);
  const videos = useMemo(() => videosQ.data?.videos ?? [], [videosQ.data]);

  const onPick = async (v: (typeof videos)[number]) => {
    setBusyVideoId(v.id);
    try {
      const created = await add.mutateAsync({ videoId: v.id });
      onAdded(created.id, {
        videoId: v.id,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl,
        durationSec: v.durationSec,
      });
      onClose();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Couldn't add that video.");
    } finally {
      setBusyVideoId(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a video"
      eyebrow="Series · add"
      size="lg"
    >
      <input
        type="search"
        className="auth-input"
        placeholder="Search the channel…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <ul className="yt-row-list mt-4">
        {videos.map((v) => (
          <li key={v.id} className="yt-row">
            <div className="yt-row-thumb">
              <img src={v.thumbnailUrl} alt="" loading="lazy" />
            </div>
            <div className="yt-row-body">
              <p className="yt-row-title">{v.title}</p>
              <p className="yt-row-meta">
                <span className="font-grotesk text-[0.62rem]">{v.id}</span>
                <span className="yt-row-sep">·</span>
                <span>{v.privacyStatus ?? "?"}</span>
              </p>
            </div>
            <div className="yt-row-actions">
              <button
                type="button"
                className="btn-app"
                onClick={() => onPick(v)}
                disabled={busyVideoId === v.id}
              >
                {busyVideoId === v.id ? "Adding…" : "Add"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
